import { Seo } from '../components/Seo';
import { buildTitle, SITE } from '../lib/site';

const LAST_UPDATED = '2026-07-13';

export function AmlKycPage() {
  return (
    <>
      <Seo
        title={buildTitle('AML / KYC Policy')}
        description="ShieldMyAss AML/KYC Policy: no identity verification for standard swaps, AML monitoring on flagged transactions, full compliance with FATF travel rule spirit without compromising non-custodial model."
        path="/aml-kyc-policy"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'AML / KYC Policy',
          description:
            'Anti-Money-Laundering and Know-Your-Customer policy for the ShieldMyAss non-custodial crypto swap service.',
          url: `${SITE.url}/aml-kyc-policy`,
          inLanguage: 'en',
          dateModified: LAST_UPDATED,
          publisher: { '@type': 'Organization', name: SITE.name, url: SITE.url },
        }}
      />

      <section className="page-header">
        <div className="container">
          <span className="eyebrow">Compliance</span>
          <h1>AML / KYC Policy</h1>
          <p>Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="prose">
            <div className="callout">
              <div className="title">TL;DR</div>
              <p>
                <strong>{SITE.name}</strong> is a non-custodial instant cryptocurrency swap service. We do not
                require identity verification (KYC) for standard transactions. Our liquidity partner applies
                risk-based AML monitoring and may request additional information only when legally required
                for specific flagged transactions.
              </p>
            </div>

            <h2>1. Service model</h2>
            <p>
              {SITE.name} operates as a non-custodial cryptocurrency swap facilitator. We do not hold user
              balances, custody funds, or maintain user accounts. Funds move directly from the sender's
              wallet, through our regulated liquidity partner's network, to the receiver's wallet. Each swap
              is atomic and identified by a one-time deposit address and order id.
            </p>
            <p>
              Because {SITE.name} does not take custody of user funds at any point, we are not classified as
              a Money Services Business (MSB), Virtual Asset Service Provider (VASP) custodian, or
              financial institution under most jurisdictions. The AML/KYC obligations applicable to
              custodial exchanges do not extend to our service model.
            </p>

            <h2>2. KYC policy</h2>
            <p>
              <strong>{SITE.name} does not collect Know-Your-Customer information.</strong> Users are not
              required to provide:
            </p>
            <ul>
              <li>Government-issued identity documents</li>
              <li>Proof of address</li>
              <li>Selfies or biometric data</li>
              <li>Tax identification numbers</li>
              <li>Email addresses or phone numbers (an optional contact email may be provided per swap for status notifications only)</li>
            </ul>
            <p>
              Account creation is never required to initiate or complete a swap. The user-facing interface
              operates without authentication of any kind.
            </p>

            <h2>3. AML monitoring</h2>
            <p>
              While {SITE.name} itself does not perform transaction monitoring, all swaps are routed
              through our regulated liquidity partner ({SITE.url.replace('https://www.', '')} aggregates
              liquidity from a partner network). The partner applies:
            </p>
            <ul>
              <li>Blockchain analytics on deposit and payout addresses</li>
              <li>Sanctions screening (OFAC SDN, UN, EU, UK HM Treasury consolidated lists)</li>
              <li>Darknet-market, mixer, and illicit-service cluster heuristics</li>
              <li>Risk scoring based on transaction patterns and counterparties</li>
            </ul>
            <p>
              If a swap is flagged by the partner's risk engine, the partner (not {SITE.name}) may
              request additional information from the user directly. This occurs only for a small minority
              of transactions and is handled between the user and the partner.
            </p>

            <h2>4. Restricted jurisdictions</h2>
            <p>
              {SITE.name} does not knowingly provide services to individuals or entities located in,
              incorporated in, or ordinarily resident in:
            </p>
            <ul>
              <li>The United States of America and its territories</li>
              <li>Any country subject to comprehensive sanctions by the United Nations, European Union, United Kingdom, or United States (including but not limited to Cuba, Iran, North Korea, Syria, Crimea/DNR/LNR regions of Ukraine)</li>
            </ul>
            <p>
              Users are responsible for ensuring their use of {SITE.name} complies with the laws of their
              jurisdiction.
            </p>

            <h2>5. Prohibited use</h2>
            <p>Users may not use {SITE.name} in connection with:</p>
            <ul>
              <li>Money laundering, terrorist financing, or sanctions evasion</li>
              <li>Proceeds of crime, fraud, ransomware, or illicit goods</li>
              <li>Mixing or tumbling services in a manner designed to obscure illicit origin</li>
              <li>Any activity that would violate FATF Recommendations or applicable local law</li>
            </ul>
            <p>
              Suspected prohibited use is referred to our liquidity partner, who may freeze, refund, or
              report the transaction to the relevant Financial Intelligence Unit (FIU) in line with their
              licensing obligations.
            </p>

            <h2>6. Record keeping</h2>
            <p>
              {SITE.name} retains minimal operational logs: order identifiers, deposit addresses,
              timestamps, transaction hashes, and partner-provided status events. These logs are retained
              for 90 days for customer-support purposes and then purged. We do not retain personal data
              (none is collected).
            </p>
            <p>
              Our liquidity partner maintains fuller records in line with their regulatory licensing.
            </p>

            <h2>7. Reporting &amp; cooperation</h2>
            <p>
              {SITE.name} cooperates with lawful requests from competent authorities. Because we hold no
              user funds and collect no KYC data, our ability to comply is limited to providing the
              operational logs described in section 6. Requests should be directed to
              {' '}<a href={`mailto:legal@${SITE.domain}`}>legal@{SITE.domain}</a>.
            </p>

            <h2>8. Sanctions screening</h2>
            <p>
              Deposit and payout addresses submitted by users are screened against OFAC SDN, UN, EU and UK
              consolidated sanctions lists via our liquidity partner's infrastructure prior to swap
              execution. Matches result in automatic swap cancellation and refund to the sender's refund
              address (or original deposit address if no refund address was provided).
            </p>

            <h2>9. Travel Rule</h2>
            <p>
              FATF Recommendation 16 (the "Travel Rule") applies to VASPs sending or receiving virtual
              asset transfers on behalf of customers. Because {SITE.name} does not maintain customer
              accounts and never takes custody of user funds, the Travel Rule does not apply to our
              service. Our partner handles any Travel Rule obligations on their side of the swap.
            </p>

            <h2>10. Risk assessment &amp; staff training</h2>
            <p>
              {SITE.name}'s AML risk posture is reviewed annually. Staff with access to operational systems
              receive training on sanctions screening, typologies of crypto-based money laundering, and
              escalation procedures.
            </p>

            <h2>11. Changes to this policy</h2>
            <p>
              We may update this policy from time to time. Material changes will be announced on the
              {SITE.name} status channel. Continued use of the service after a change constitutes
              acceptance of the updated policy.
            </p>

            <h2>12. Contact</h2>
            <p>
              Questions about this policy: <a href={`mailto:legal@${SITE.domain}`}>legal@{SITE.domain}</a><br />
              General support: <a href={`mailto:${SITE.email}`}>{SITE.email}</a><br />
              Security disclosures: <a href={`mailto:security@${SITE.domain}`}>security@{SITE.domain}</a>
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
